import { useState, useEffect } from "react";
import { DatabaseOutlined } from "@ant-design/icons";
import { apiUtils } from "../lib/api";
import LoadingSpinner from "../components/LoadingSpinner";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { setCurrentProject } from "../store/slices/projectSlice";


interface DataSource {
  id: string;
  name: string;
  description?: string;
  connectorType: string;
  status: string;
  lastSyncStatus: string;
  lastSyncAt?: string;
  documentsCount: number;
  projectId: string;
  autoSync?: boolean;
  syncSchedule?: string;
  config?: Record<string, any>;
}

const ProjectsPage = () => {
  // const { user } = useAuth(); // Currently not used but may be needed for future features
  const dispatch = useAppDispatch();
  const { currentProject: selectedProject, projects, isLoading: projectsLoading } = useAppSelector((state) => state.project);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showCreateDataSource, setShowCreateDataSource] = useState(false);
  const [showEditDataSource, setShowEditDataSource] = useState(false);
  const [selectedDataSource, setSelectedDataSource] =
    useState<DataSource | null>(null);
  const [syncSchedule, setSyncSchedule] = useState("");
  const [testConnectionResult, setTestConnectionResult] = useState<any>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [databaseSchema, setDatabaseSchema] = useState<any>(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [tableSearchTerm, setTableSearchTerm] = useState("");
  const [syncProgress, setSyncProgress] = useState<Record<string, {
    progress: number;
    current: number;
    total: number;
    status: string;
  }>>({});
  const [eventSources, setEventSources] = useState<Record<string, EventSource>>({});

  // Project creation form state
  const [projectForm, setProjectForm] = useState({
    name: "",
    description: "",
  });

  // Data source creation form state
  const [dataSourceForm, setDataSourceForm] = useState({
    name: "",
    description: "",
    connectorType: "file",
    config: {} as Record<string, any>,
  });

  const [availableConnectors, setAvailableConnectors] = useState<
    Array<{
      type: string;
      name: string;
      description: string;
    }>
  >([]);

  useEffect(() => {
    fetchAvailableConnectors();
  }, []);

  // Cleanup EventSource connections on unmount
  useEffect(() => {
    return () => {
      Object.values(eventSources).forEach(es => es.close());
    };
  }, [eventSources]);

  useEffect(() => {
    if (selectedProject) {
      fetchDataSources(selectedProject.id);
    } else {
      setDataSources([]);
    }
  }, [selectedProject]);


  const fetchDataSources = async (projectId: string) => {
    try {
      const response = await apiUtils.backend.get(
        `/projects/${projectId}/data-sources`
      );
      setDataSources(response.data);

      // Update selectedDataSource if it exists in the updated data
      if (selectedDataSource) {
        const updatedDataSource = response.data.find(
          (ds: DataSource) => ds.id === selectedDataSource.id
        );
        if (updatedDataSource) {
          setSelectedDataSource(updatedDataSource);
        }
      }
    } catch (err: any) {
      console.error("Error fetching data sources:", err);
    }
  };

  const fetchAvailableConnectors = async () => {
    try {
      const response = await apiUtils.backend.get("/data-sources/connectors");
      setAvailableConnectors(response.data);
    } catch (err: any) {
      console.error("Error fetching connectors:", err);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiUtils.backend.post("/projects", projectForm);
      const newProject = response.data;
      dispatch(setCurrentProject(newProject));
      setProjectForm({ name: "", description: "" });
      setShowCreateProject(false);
    } catch (err: any) {
      setError("Failed to create project");
      console.error("Error creating project:", err);
    }
  };

  const handleCreateDataSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      const response = await apiUtils.backend.post(
        `/projects/${selectedProject.id}/data-sources`,
        dataSourceForm
      );
      setDataSources([response.data, ...dataSources]);
      setDataSourceForm({
        name: "",
        description: "",
        connectorType: "file",
        config: {},
      });
      setShowCreateDataSource(false);
    } catch (err: any) {
      setError("Failed to create data source");
      console.error("Error creating data source:", err);
    }
  };

  const handleSyncDataSource = async (dataSourceId: string) => {
    if (!selectedProject) return;

    try {
      await apiUtils.backend.post(
        `/projects/${selectedProject.id}/data-sources/${dataSourceId}/sync`
      );
      fetchDataSources(selectedProject.id);
    } catch (err: any) {
      setError("Failed to sync data source");
      console.error("Error syncing data source:", err);
    }
  };

  const handleEditDataSource = (dataSource: DataSource) => {
    setSelectedDataSource(dataSource);
    setDataSourceForm({
      name: dataSource.name,
      description: dataSource.description || "",
      connectorType: dataSource.connectorType,
      config: dataSource.config || {},
    });
    setSyncSchedule(dataSource.syncSchedule || "0 */1 * * *");
    setTestConnectionResult(null);
    setDatabaseSchema(null);
    setTableSearchTerm("");

    // Auto-load schema if connection config is complete and tables are configured
    if (
      dataSource.connectorType === "mssql" &&
      dataSource.config?.server &&
      dataSource.config?.database &&
      dataSource.config?.user &&
      dataSource.config?.password
    ) {
      // Set successful test connection result to show schema loading
      setTestConnectionResult({
        success: true,
        message: "Connection configured",
      });

      // Auto-load schema if tables are already configured
      if (dataSource.config?.tables && dataSource.config.tables.length > 0) {
        setTimeout(() => {
          handleLoadDatabaseSchemaForEdit(dataSource.config!);
        }, 100);
      }
    }

    setShowEditDataSource(true);
  };

  const handleOpenCreateDataSource = () => {
    // Reset all state when opening create modal
    setDataSourceForm({
      name: "",
      description: "",
      connectorType: "file",
      config: {},
    });
    setTestConnectionResult(null);
    setDatabaseSchema(null);
    setShowCreateDataSource(true);
  };

  const handleUpdateDataSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !selectedDataSource) return;

    try {
      const response = await apiUtils.backend.put(
        `/projects/${selectedProject.id}/data-sources/${selectedDataSource.id}`,
        {
          ...dataSourceForm,
          syncSchedule: syncSchedule,
        }
      );

      // Update the data source in the local state
      setDataSources(
        dataSources.map((ds) =>
          ds.id === selectedDataSource.id ? response.data : ds
        )
      );

      // Close modal immediately (non-blocking)
      setShowEditDataSource(false);
      setSelectedDataSource(null);
      setDataSourceForm({
        name: "",
        description: "",
        connectorType: "file",
        config: {},
      });
      setSyncSchedule("");
      setTestConnectionResult(null);

      // Start schema sync in background
      try {
        const syncResponse = await apiUtils.backend.post('/mcp/schema/sync', {
          dataSourceId: response.data.id,
        });

        if (syncResponse.data.success) {
          // Initialize progress state
          setSyncProgress(prev => ({
            ...prev,
            [response.data.id]: { progress: 0, current: 0, total: 0, status: 'syncing' }
          }));

          // Connect to SSE for real-time progress
          const eventSource = new EventSource(
            `${import.meta.env.VITE_API_BASE_URL || ' http://localhost:8000'}/api/v1/mcp/sse/sync/${response.data.id}`
          );

          eventSource.onmessage = (event) => {
            const progress = JSON.parse(event.data);
            
            // Update progress state
            setSyncProgress(prev => ({
              ...prev,
              [response.data.id]: {
                progress: progress.progress || 0,
                current: progress.current || 0,
                total: progress.total || 0,
                status: progress.syncStatus,
              }
            }));

            // Close connection when done
            if (progress.syncStatus !== 'syncing') {
              eventSource.close();
              setEventSources(prev => {
                const newSources = { ...prev };
                delete newSources[response.data.id];
                return newSources;
              });

              // Refresh data sources to show updated status
              setTimeout(() => fetchDataSources(selectedProject.id), 1000);
            }
          };

          eventSource.onerror = () => {
            console.error('SSE connection error');
            eventSource.close();
            setSyncProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[response.data.id];
              return newProgress;
            });
          };

          // Store event source for cleanup
          setEventSources(prev => ({ ...prev, [response.data.id]: eventSource }));
        }
      } catch (syncError: any) {
        console.error('Failed to start schema sync:', syncError);
      }
    } catch (err: any) {
      setError("Failed to update data source");
      console.error("Error updating data source:", err);
    }
  };

  const handleDeleteDataSource = async (dataSourceId: string) => {
    if (!selectedProject) return;

    if (
      !confirm(
        "Are you sure you want to delete this data source? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await apiUtils.backend.delete(
        `/projects/${selectedProject.id}/data-sources/${dataSourceId}`
      );
      setDataSources(dataSources.filter((ds) => ds.id !== dataSourceId));
    } catch (err: any) {
      setError("Failed to delete data source");
      console.error("Error deleting data source:", err);
    }
  };

  const handleToggleAutoSync = async (
    dataSourceId: string,
    enabled: boolean
  ) => {
    if (!selectedProject) return;

    try {
      if (enabled) {
        await apiUtils.backend.post(
          `/projects/${selectedProject.id}/data-sources/${dataSourceId}/auto-sync/enable`,
          {
            schedule: syncSchedule || "0 */1 * * *", // Use current sync schedule or default
          }
        );
      } else {
        await apiUtils.backend.post(
          `/projects/${selectedProject.id}/data-sources/${dataSourceId}/auto-sync/disable`
        );
      }

      // Update selectedDataSource immediately for UI responsiveness
      if (selectedDataSource && selectedDataSource.id === dataSourceId) {
        setSelectedDataSource({
          ...selectedDataSource,
          autoSync: enabled,
          syncSchedule: enabled
            ? syncSchedule || "0 */1 * * *"
            : selectedDataSource.syncSchedule,
        });
      }

      fetchDataSources(selectedProject.id);
    } catch (err: any) {
      setError("Failed to toggle auto-sync");
      console.error("Error toggling auto-sync:", err);
    }
  };

  const handleUpdateStatus = async (dataSourceId: string, status: string) => {
    if (!selectedProject) return;

    try {
      await apiUtils.backend.put(
        `/projects/${selectedProject.id}/data-sources/${dataSourceId}/status`,
        {
          status,
        }
      );

      // Update selectedDataSource immediately for UI responsiveness
      if (selectedDataSource && selectedDataSource.id === dataSourceId) {
        setSelectedDataSource({
          ...selectedDataSource,
          status: status,
        });
      }

      fetchDataSources(selectedProject.id);
    } catch (err: any) {
      setError("Failed to update status");
      console.error("Error updating status:", err);
    }
  };

  // handleUpdateSyncSchedule function is currently unused but may be needed for future features
  // const handleUpdateSyncSchedule = async (
  //   dataSourceId: string,
  //   schedule: string
  // ) => {
  //   if (!selectedProject) return;
  //   try {
  //     await apiUtils.backend.put(
  //       `/projects/${selectedProject.id}/data-sources/${dataSourceId}`,
  //       { syncSchedule: schedule }
  //     );
  //     fetchDataSources(selectedProject.id);
  //   } catch (err: any) {
  //     setError("Failed to update sync schedule");
  //     console.error("Error updating sync schedule:", err);
  //   }
  // };

  const handleTestConnection = async () => {
    if (!selectedProject) return;

    setTestingConnection(true);
    setTestConnectionResult(null);

    try {
      // Use direct test connection endpoint for both create and edit modes
      const response = await apiUtils.backend.post(
        `/data-sources/test-connection`,
        {
          connectorType: dataSourceForm.connectorType,
          config: dataSourceForm.config,
        }
      );
      setTestConnectionResult(response.data);
    } catch (err: any) {
      setTestConnectionResult({
        success: false,
        message: "Test connection failed",
        error: err.response?.data?.message || err.message,
      });
      console.error("Error testing connection:", err);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleLoadDatabaseSchema = async () => {
    if (!selectedProject) return;

    setLoadingSchema(true);
    setDatabaseSchema(null);

    try {
      // Use direct schema endpoint for both create and edit modes
      const response = await apiUtils.backend.post(
        `/data-sources/schema`,
        {
          connectorType: dataSourceForm.connectorType,
          config: dataSourceForm.config,
        },
        {
          timeout: 60000, // 60 second timeout to match backend
        }
      );
      setDatabaseSchema(response.data);
    } catch (err: any) {
      setDatabaseSchema({
        success: false,
        message: "Failed to load database schema",
        error: err.response?.data?.message || err.message,
      });
      console.error("Error loading database schema:", err);
    } finally {
      setLoadingSchema(false);
    }
  };

  const handleLoadDatabaseSchemaForEdit = async (
    config: Record<string, any>
  ) => {
    if (!selectedProject) return;

    setLoadingSchema(true);
    setDatabaseSchema(null);

    try {
      const response = await apiUtils.backend.post(
        `/data-sources/schema`,
        {
          connectorType: dataSourceForm.connectorType,
          config: config,
        },
        {
          timeout: 60000,
        }
      );
      setDatabaseSchema(response.data);
    } catch (err: any) {
      setDatabaseSchema({
        success: false,
        message: "Failed to load database schema",
        error: err.response?.data?.message || err.message,
      });
      console.error("Error loading database schema:", err);
    } finally {
      setLoadingSchema(false);
    }
  };

  const handleSelectAllTables = (select: boolean) => {
    if (!databaseSchema?.schema?.tables) return;

    const filteredTables = databaseSchema.schema.tables.filter(
      (table: any) =>
        !tableSearchTerm ||
        table.name.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
        table.schema.toLowerCase().includes(tableSearchTerm.toLowerCase())
    );

    const currentTables = dataSourceForm.config.tables || [];

    if (select) {
      // Add all filtered tables
      const newTables = [...currentTables];
      filteredTables.forEach((table: any) => {
        if (!newTables.some((t: any) => t.name === table.name)) {
          const textColumns = table.columns
            .filter((col: any) => col.isTextColumn)
            .map((col: any) => col.name);

          newTables.push({
            name: table.name,
            schema: table.schema,
            textColumns: textColumns,
            keyColumn: table.columns[0]?.name || "id",
          });
        }
      });

      setDataSourceForm({
        ...dataSourceForm,
        config: {
          ...dataSourceForm.config,
          tables: newTables,
        },
      });
    } else {
      // Remove all filtered tables
      const remainingTables = currentTables.filter(
        (currentTable: any) =>
          !filteredTables.some((table: any) => table.name === currentTable.name)
      );

      setDataSourceForm({
        ...dataSourceForm,
        config: {
          ...dataSourceForm.config,
          tables: remainingTables,
        },
      });
    }
  };

  const handleSelectAllColumns = (tableName: string, select: boolean) => {
    const currentTables = dataSourceForm.config.tables || [];
    const table = databaseSchema.schema?.tables?.find(
      (t: any) => t.name === tableName
    );

    if (!table) return;

    const updatedTables = currentTables.map((t: any) => {
      if (t.name === tableName) {
        if (select) {
          // Select all text columns
          const allTextColumns = table.columns
            .filter((col: any) => col.isTextColumn)
            .map((col: any) => col.name);

          return {
            ...t,
            textColumns: allTextColumns,
          };
        } else {
          // Deselect all columns
          return {
            ...t,
            textColumns: [],
          };
        }
      }
      return t;
    });

    setDataSourceForm({
      ...dataSourceForm,
      config: {
        ...dataSourceForm.config,
        tables: updatedTables,
      },
    });
  };

  const renderFileConnectorConfig = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Base Path
        </label>
        <input
          type="text"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="/path/to/documents"
          value={dataSourceForm.config.basePath || ""}
          onChange={(e) =>
            setDataSourceForm({
              ...dataSourceForm,
              config: { ...dataSourceForm.config, basePath: e.target.value },
            })
          }
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          File Extensions
        </label>
        <input
          type="text"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder=".txt,.md,.json,.csv"
          value={
            dataSourceForm.config.extensions?.join(",") || ".txt,.md,.json,.csv"
          }
          onChange={(e) =>
            setDataSourceForm({
              ...dataSourceForm,
              config: {
                ...dataSourceForm.config,
                extensions: e.target.value.split(",").map((ext) => ext.trim()),
              },
            })
          }
        />
      </div>
    </div>
  );

  const renderMsSqlConnectorConfig = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Server
        </label>
        <input
          type="text"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="localhost"
          value={dataSourceForm.config.server || ""}
          onChange={(e) =>
            setDataSourceForm({
              ...dataSourceForm,
              config: { ...dataSourceForm.config, server: e.target.value },
            })
          }
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Port</label>
        <input
          type="number"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="1433"
          value={dataSourceForm.config.port || ""}
          onChange={(e) =>
            setDataSourceForm({
              ...dataSourceForm,
              config: {
                ...dataSourceForm.config,
                port: parseInt(e.target.value) || 1433,
              },
            })
          }
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Database
        </label>
        <input
          type="text"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="mydb"
          value={dataSourceForm.config.database || ""}
          onChange={(e) =>
            setDataSourceForm({
              ...dataSourceForm,
              config: { ...dataSourceForm.config, database: e.target.value },
            })
          }
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Username
        </label>
        <input
          type="text"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="username"
          value={dataSourceForm.config.user || ""}
          onChange={(e) =>
            setDataSourceForm({
              ...dataSourceForm,
              config: { ...dataSourceForm.config, user: e.target.value },
            })
          }
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Password
        </label>
        <input
          type="password"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="password"
          value={dataSourceForm.config.password || ""}
          onChange={(e) =>
            setDataSourceForm({
              ...dataSourceForm,
              config: { ...dataSourceForm.config, password: e.target.value },
            })
          }
        />
      </div>

      {/* Test Connection Section */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Connection Test
          </label>
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={
              testingConnection ||
              !dataSourceForm.config.server ||
              !dataSourceForm.config.database ||
              !dataSourceForm.config.user ||
              !dataSourceForm.config.password
            }
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testingConnection ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </button>
        </div>

        {testConnectionResult && (
          <div
            className={`mt-3 p-3 rounded-md ${testConnectionResult.success
              ? "bg-green-50 border border-green-200"
              : "bg-red-50 border border-red-200"
              }`}
          >
            <div className="flex items-center">
              {testConnectionResult.success ? (
                <svg
                  className="h-5 w-5 text-green-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <div className="ml-3">
                <p
                  className={`text-sm font-medium ${testConnectionResult.success
                    ? "text-green-800"
                    : "text-red-800"
                    }`}
                >
                  {testConnectionResult.message}
                </p>
                {testConnectionResult.error && (
                  <p className="text-sm text-red-600 mt-1">
                    Error: {testConnectionResult.error}
                  </p>
                )}
                {testConnectionResult.details && (
                  <div className="mt-2 text-xs text-gray-600">
                    <p>
                      Connection time:{" "}
                      {testConnectionResult.details.connectionTime}
                    </p>
                    <p>
                      Host: {testConnectionResult.details.host}:
                      {testConnectionResult.details.port}
                    </p>
                    <p>Database: {testConnectionResult.details.database}</p>
                    <p>User: {testConnectionResult.details.user}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Database Schema and Table Selection */}
      {testConnectionResult?.success && (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Database Tables
            </label>
            <button
              type="button"
              onClick={handleLoadDatabaseSchema}
              disabled={loadingSchema}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loadingSchema ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading...
                </>
              ) : (
                "Load Tables"
              )}
            </button>
          </div>

          {databaseSchema && (
            <div className="mt-4">
              {databaseSchema.success ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">
                    Database: {databaseSchema.schema?.database} on{" "}
                    {databaseSchema.schema?.server}
                    <span className="ml-4">
                      ({databaseSchema.schema?.tables?.length || 0} tables
                      found)
                    </span>
                  </div>

                  {/* Search and Select All Controls */}
                  <div className="flex items-center justify-between space-x-4 mb-4">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Search tables..."
                        value={tableSearchTerm}
                        onChange={(e) => setTableSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleSelectAllTables(true)}
                        className="px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-300 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSelectAllTables(false)}
                        className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>

                  <div className="max-h-96 overflow-y-auto border rounded-md">
                    {databaseSchema.schema?.tables?.length > 0 ? (
                      databaseSchema.schema.tables
                        .filter(
                          (table: any) =>
                            !tableSearchTerm ||
                            table.name
                              .toLowerCase()
                              .includes(tableSearchTerm.toLowerCase()) ||
                            table.schema
                              .toLowerCase()
                              .includes(tableSearchTerm.toLowerCase())
                        )
                        .map((table: any, tableIndex: number) => (
                          <div
                            key={`${table.schema}.${table.name}`}
                            className="border-b last:border-b-0"
                          >
                            <div className="p-3 bg-gray-50">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="checkbox"
                                    id={`table-${tableIndex}`}
                                    checked={
                                      dataSourceForm.config.tables?.some(
                                        (t: any) => t.name === table.name
                                      ) || false
                                    }
                                    onChange={(e) => {
                                      const currentTables =
                                        dataSourceForm.config.tables || [];
                                      if (e.target.checked) {
                                        // Add table with default text columns
                                        const textColumns = table.columns
                                          .filter(
                                            (col: any) => col.isTextColumn
                                          )
                                          .map((col: any) => col.name);

                                        const newTable = {
                                          name: table.name,
                                          schema: table.schema,
                                          textColumns: textColumns,
                                          keyColumn:
                                            table.columns[0]?.name || "id",
                                        };

                                        setDataSourceForm({
                                          ...dataSourceForm,
                                          config: {
                                            ...dataSourceForm.config,
                                            tables: [
                                              ...currentTables,
                                              newTable,
                                            ],
                                          },
                                        });
                                      } else {
                                        // Remove table
                                        setDataSourceForm({
                                          ...dataSourceForm,
                                          config: {
                                            ...dataSourceForm.config,
                                            tables: currentTables.filter(
                                              (t: any) => t.name !== table.name
                                            ),
                                          },
                                        });
                                      }
                                    }}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                  />
                                  <label
                                    htmlFor={`table-${tableIndex}`}
                                    className="text-sm font-medium text-gray-900"
                                  >
                                    {table.schema}.{table.name}
                                  </label>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {table.columns.length} columns
                                </span>
                              </div>
                            </div>

                            {/* Column selection for selected tables */}
                            {dataSourceForm.config.tables?.some(
                              (t: any) => t.name === table.name
                            ) && (
                                <div className="p-3 bg-white">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs font-medium text-gray-700">
                                      Text Columns to Index:
                                    </div>
                                    <div className="flex space-x-1">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleSelectAllColumns(table.name, true)
                                        }
                                        className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                      >
                                        Select All
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleSelectAllColumns(
                                            table.name,
                                            false
                                          )
                                        }
                                        className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-500"
                                      >
                                        None
                                      </button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    {table.columns.map(
                                      (column: any, colIndex: number) => (
                                        <div
                                          key={colIndex}
                                          className="flex items-center space-x-2"
                                        >
                                          <input
                                            type="checkbox"
                                            id={`col-${tableIndex}-${colIndex}`}
                                            checked={
                                              dataSourceForm.config.tables
                                                ?.find(
                                                  (t: any) =>
                                                    t.name === table.name
                                                )
                                                ?.textColumns?.includes(
                                                  column.name
                                                ) || false
                                            }
                                            onChange={(e) => {
                                              const currentTables =
                                                dataSourceForm.config.tables ||
                                                [];
                                              const updatedTables =
                                                currentTables.map((t: any) => {
                                                  if (t.name === table.name) {
                                                    const currentColumns =
                                                      t.textColumns || [];
                                                    return {
                                                      ...t,
                                                      textColumns: e.target
                                                        .checked
                                                        ? [
                                                          ...currentColumns,
                                                          column.name,
                                                        ]
                                                        : currentColumns.filter(
                                                          (col: string) =>
                                                            col !== column.name
                                                        ),
                                                    };
                                                  }
                                                  return t;
                                                });

                                              setDataSourceForm({
                                                ...dataSourceForm,
                                                config: {
                                                  ...dataSourceForm.config,
                                                  tables: updatedTables,
                                                },
                                              });
                                            }}
                                            className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                          />
                                          <label
                                            htmlFor={`col-${tableIndex}-${colIndex}`}
                                            className="text-xs text-gray-600"
                                          >
                                            {column.name}
                                            <span className="ml-1 text-gray-400">
                                              ({column.dataType})
                                            </span>
                                          </label>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        No tables found
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center">
                    <svg
                      className="h-5 w-5 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">
                        {databaseSchema.message}
                      </p>
                      {databaseSchema.error && (
                        <p className="text-sm text-red-600 mt-1">
                          Error: {databaseSchema.error}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (projectsLoading && projects.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Data Sources</h1>
            {selectedProject && (
              <p className="text-gray-600 mt-1">
                Managing data sources for {selectedProject.name}
              </p>
            )}
          </div>
          {selectedProject && (
            <button
              onClick={handleOpenCreateDataSource}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all hover:opacity-90"
              style={{ backgroundColor: 'var(--primary-color, #13afe3)' }}
            >
              Add Data Source
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {!selectedProject ? (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-8 text-center text-gray-500">
              <DatabaseOutlined className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Project Selected</h3>
              <p>Please select a project from the header to view and manage data sources.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Data Sources for {selectedProject.name}
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {projectsLoading ? (
                <div className="px-4 py-8 text-center">
                  <LoadingSpinner />
                </div>
              ) : dataSources.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  No data sources found. Create one to get started.
                </div>
              ) : (
                dataSources.map((dataSource) => (
                  <div
                    key={dataSource.id}
                    className="px-4 py-4 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {dataSource.name}
                          </p>
                          <span
                            className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${dataSource.connectorType === "file"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-purple-100 text-purple-800"
                              }`}
                          >
                            {dataSource.connectorType}
                          </span>
                          <span
                            className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${dataSource.status === "active"
                              ? "bg-green-100 text-green-800"
                              : dataSource.status === "inactive"
                                ? "bg-gray-100 text-gray-800"
                                : "bg-red-100 text-red-800"
                              }`}
                          >
                            {dataSource.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate mt-1">
                          {dataSource.description || "No description"}
                        </p>

                        <div className="flex items-center mt-2 text-xs text-gray-500 space-x-4">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full ${dataSource.lastSyncStatus === "success"
                              ? "bg-green-100 text-green-800"
                              : dataSource.lastSyncStatus === "error"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                              }`}
                          >
                            {dataSource.lastSyncStatus}
                          </span>
                          <span>{dataSource.documentsCount} documents</span>
                          {dataSource.lastSyncAt && (
                            <span>
                              Last sync:{" "}
                              {new Date(
                                dataSource.lastSyncAt
                              ).toLocaleString()}
                            </span>
                          )}
                          {dataSource.autoSync && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                              Auto-sync ON
                            </span>
                          )}
                        </div>

                        {/* Real-time Sync Progress Bar */}
                        {syncProgress[dataSource.id] && syncProgress[dataSource.id].status === 'syncing' && (
                          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-blue-800">
                                Syncing schema...
                              </span>
                              <span className="text-xs text-blue-600">
                                {syncProgress[dataSource.id].current} / {syncProgress[dataSource.id].total} tables
                              </span>
                            </div>
                            <div className="w-full bg-blue-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${syncProgress[dataSource.id].progress}%` }}
                              />
                            </div>
                            <div className="text-xs text-blue-700 mt-1 text-center">
                              {syncProgress[dataSource.id].progress}%
                            </div>
                          </div>
                        )}

                        {/* Configuration preview */}
                        <div className="mt-2 text-xs text-gray-400">
                          {dataSource.connectorType === "file" && (
                            <span>
                              Path:{" "}
                              {dataSource.config?.basePath ||
                                "Not configured"}
                            </span>
                          )}
                          {dataSource.connectorType === "mssql" && (
                            <span>
                              Server:{" "}
                              {dataSource.config?.server ||
                                "Not configured"}
                              {dataSource.config?.port
                                ? `:${dataSource.config.port}`
                                : ":1433"}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex-shrink-0 ml-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() =>
                              handleSyncDataSource(dataSource.id)
                            }
                            className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            disabled={dataSource.status === "inactive"}
                          >
                            Sync
                          </button>
                          <button
                            onClick={() => handleEditDataSource(dataSource)}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Settings
                          </button>
                          <div className="relative">
                            <select
                              value={dataSource.status}
                              onChange={(e) =>
                                handleUpdateStatus(
                                  dataSource.id,
                                  e.target.value
                                )
                              }
                              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 mt-2">
                          <label className="flex items-center text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={dataSource.autoSync || false}
                              onChange={(e) =>
                                handleToggleAutoSync(
                                  dataSource.id,
                                  e.target.checked
                                )
                              }
                              className="mr-1 h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            Auto-sync
                          </label>
                          <button
                            onClick={() =>
                              handleDeleteDataSource(dataSource.id)
                            }
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateProject && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]">
          <div
            className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-[10000] overflow-y-auto"
            style={{
              transform: showCreateProject ? 'translateX(0)' : 'translateX(100%)'
            }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Create New Project
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Add a new project to organize your data sources and documents
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateProject(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreateProject}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Name
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={projectForm.name}
                    onChange={(e) =>
                      setProjectForm({ ...projectForm, name: e.target.value })
                    }
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    rows={3}
                    value={projectForm.description}
                    onChange={(e) =>
                      setProjectForm({
                        ...projectForm,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateProject(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all hover:opacity-90"
                    style={{ backgroundColor: 'var(--primary-color, #13afe3)' }}
                  >
                    Create Project
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Data Source Modal */}
      {showCreateDataSource && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]">
          <div
            className="fixed right-0 top-0 h-full w-[600px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-[10000] overflow-y-auto"
            style={{
              transform: showCreateDataSource ? 'translateX(0)' : 'translateX(100%)'
            }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Add Data Source
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Connect a new data source to {selectedProject?.name}
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateDataSource(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreateDataSource}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data Source Name
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={dataSourceForm.name}
                    onChange={(e) =>
                      setDataSourceForm({
                        ...dataSourceForm,
                        name: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    rows={2}
                    value={dataSourceForm.description}
                    onChange={(e) =>
                      setDataSourceForm({
                        ...dataSourceForm,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Connector Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={dataSourceForm.connectorType}
                    onChange={(e) =>
                      setDataSourceForm({
                        ...dataSourceForm,
                        connectorType: e.target.value,
                        config: {},
                      })
                    }
                  >
                    {availableConnectors.map((connector) => (
                      <option key={connector.type} value={connector.type}>
                        {connector.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Configuration Section */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-800 mb-3">
                    Configuration
                  </h4>
                  {dataSourceForm.connectorType === "file" &&
                    renderFileConnectorConfig()}
                  {dataSourceForm.connectorType === "mssql" &&
                    renderMsSqlConnectorConfig()}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateDataSource(false)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all hover:opacity-90"
                    style={{ backgroundColor: 'var(--primary-color, #13afe3)' }}
                  >
                    Create Data Source
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Data Source Modal */}
      {showEditDataSource && selectedDataSource && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]">
          <div
            className="fixed right-0 top-0 h-full w-[600px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-[10000] overflow-y-auto"
            style={{
              transform: showEditDataSource ? 'translateX(0)' : 'translateX(100%)'
            }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Edit Data Source
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Update settings for {selectedDataSource.name}
                  </p>
                </div>
                <button
                  onClick={() => setShowEditDataSource(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleUpdateDataSource}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data Source Name
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={dataSourceForm.name}
                    onChange={(e) =>
                      setDataSourceForm({
                        ...dataSourceForm,
                        name: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    rows={2}
                    value={dataSourceForm.description}
                    onChange={(e) =>
                      setDataSourceForm({
                        ...dataSourceForm,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Connector Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={dataSourceForm.connectorType}
                    onChange={(e) =>
                      setDataSourceForm({
                        ...dataSourceForm,
                        connectorType: e.target.value,
                        config: {},
                      })
                    }
                    disabled
                  >
                    {availableConnectors.map((connector) => (
                      <option key={connector.type} value={connector.type}>
                        {connector.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Connector type cannot be changed after creation
                  </p>
                </div>

                {/* Auto-sync Schedule */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auto-sync Schedule
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="0 */1 * * * (every hour)"
                    value={syncSchedule}
                    onChange={(e) => setSyncSchedule(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cron format: minute hour day month dayofweek
                  </p>
                </div>

                {/* Configuration Section */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-800 mb-3">
                    Configuration
                  </h4>
                  {dataSourceForm.connectorType === "file" &&
                    renderFileConnectorConfig()}
                  {dataSourceForm.connectorType === "mssql" &&
                    renderMsSqlConnectorConfig()}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowEditDataSource(false)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all hover:opacity-90"
                    style={{ backgroundColor: 'var(--primary-color, #13afe3)' }}
                  >
                    Update Data Source
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectsPage;
